import React, { useEffect, useState } from "react";
import { Box, Typography, IconButton, TextField, Button } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import UndoIcon from "@mui/icons-material/Undo";
import Timestamp from "./Timestamp";
import useStyles from "./KaraokeStyles";
import SyncFileInstructions from "./Components/SyncFileInstructions";
import {
  getTheNextLineThatIsNotEmpty,
  handleSpaceBar,
  removeLineStampHandler,
} from "./helper";

const SyncLyricsUI = ({
  lines,
  setLines,
  currentLine,
  setCurrentLine,
  lineTimestamps,
  setLineTimestamps,
  setTextFiledIsActive,
  setFinishedSyncing,
  audioRef,
  updateAudioTime,
  finishedSyncing,
  currentTimestamp,
  isPlaying,
  textFiledIsActive,
  playAudio,
  setIsPlaying,
}) => {
  const classes = useStyles();
  const [editingLine, setEditingLine] = useState(null); // Track which line is being edited

  const handleEditLine = (index, newText) => {
    const updatedLines = [...lines];
    updatedLines[index] = newText;
    setLines(updatedLines); // Update lines in state
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Enter") {
        setEditingLine(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [setEditingLine]);

  const removeLineTimestamp = (index) => {
    // onDelete of the time stamp remove the timeStamps of that particular line and previous line
    removeLineStampHandler(
      index,
      lineTimestamps,
      setLineTimestamps,
      updateAudioTime,
      setCurrentLine
    );
  };

  const removeLastTimestamp = () => {
    const keys = Object.keys(lineTimestamps);
    if (keys.length === 0) return;
    const lastIndex = parseInt(keys[keys.length - 1]);
    if (Number.isNaN(lastIndex)) return;
    removeLineTimestamp(lastIndex);
    setFinishedSyncing(false);
  };

  function syncNextLine() {
    if (!isPlaying) {
      alert("Please play the audio first");
      return;
    }

    // Get the next line that is not empty
    const startIndex = currentLine == null ? 0 : currentLine + 1;
    const nextLine = getTheNextLineThatIsNotEmpty(
      startIndex,
      lineTimestamps,
      lines
    );
    // Only get line after next if nextLine is valid
    const lineAfterNext = nextLine !== null && nextLine < lines.length - 1
      ? getTheNextLineThatIsNotEmpty(nextLine + 1, lineTimestamps, lines)
      : null;

    if (lineAfterNext === null && nextLine === null) {
      setFinishedSyncing(true);
    }

    // Get the current time of the audio
    const currentTime = currentTimestamp;
    const previousLineTimestamp = { ...lineTimestamps };

    // If there's no next line (we're on the last line), set the end timestamp for the current line
    if (nextLine === null && currentLine !== null) {
      if (previousLineTimestamp[currentLine]) {
        previousLineTimestamp[currentLine].end = currentTime;
      } else {
        // If current line doesn't have a timestamp yet, create it with both start and end
        previousLineTimestamp[currentLine] = {
          start: currentTime,
          end: currentTime,
        };
      }
      setLineTimestamps(previousLineTimestamp);
      setFinishedSyncing(true);
      return;
    }

    // If current line already has an end timestamp and there's no next line, just finish
    if (nextLine === null && lineTimestamps[currentLine]?.end) {
      setFinishedSyncing(true);
      return;
    }

    if (currentLine === null || currentLine < lines.length) {
      // If currentLine has a start timestamp but no end timestamp, set the end timestamp first
      if (currentLine !== null && 
          previousLineTimestamp[currentLine]?.start !== null && 
          previousLineTimestamp[currentLine]?.start !== undefined &&
          (!previousLineTimestamp[currentLine]?.end || 
           previousLineTimestamp[currentLine]?.end === null)) {
        // Set end timestamp for current line
        previousLineTimestamp[currentLine].end = currentTime;
      } else if (Object.keys(previousLineTimestamp).length > 0) {
        // Otherwise, set end timestamp for the last line in timestamps (original behavior)
        const lastLineKey = Object.keys(previousLineTimestamp)[
          Object.keys(previousLineTimestamp).length - 1
        ];
        if (previousLineTimestamp[lastLineKey] && 
            !previousLineTimestamp[lastLineKey].end) {
          previousLineTimestamp[lastLineKey].end = currentTime;
        }
      }
      
      // Set the current line to next line
      setCurrentLine(nextLine);
      
      // Create new timestamp for next line if it exists
      let newLineTimestamp = null;
      if (nextLine !== null) {
        newLineTimestamp = {
          [nextLine]: {
            start: currentTime,
          },
        };
      }

      setLineTimestamps({
        ...previousLineTimestamp,
        ...(newLineTimestamp && {
          ...newLineTimestamp,
        }),
      });
    }
  }

  useEffect(() => {
    const onSpacePress = (e) =>
      handleSpaceBar(e, finishedSyncing, syncNextLine, editingLine);

    if (currentTimestamp === audioRef.current?.duration) {
      if (!Object.keys(lineTimestamps).length) {
        return;
      }

      if (
        !lineTimestamps[
          Object.keys(lineTimestamps)[Object.keys(lineTimestamps).length - 1]
        ]?.end
      ) {
        const previousLineTimestamp = lineTimestamps;

        const previousLine =
          previousLineTimestamp[
            Object.keys(previousLineTimestamp)[
              Object.keys(previousLineTimestamp).length - 1
            ]
          ];

        if (previousLine) {
          previousLine.end = currentTimestamp;
        }

        setLineTimestamps({
          ...previousLineTimestamp,
        });
      }
    }

    if (currentTimestamp !== null && currentTimestamp !== undefined) {
      const activeLine = Object.keys(lineTimestamps).find((line) => {
        return (
          lineTimestamps[line].start <= currentTimestamp &&
          (!lineTimestamps[line].end ||
            lineTimestamps[line].end >= currentTimestamp)
        );
      });

      if (activeLine) {
        setCurrentLine(parseInt(activeLine));
      }
    }

    const currentLineElement = document.querySelector(`.line-${currentLine}`);

    if (currentLineElement) {
      currentLineElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });
    }

    const lengthOfLinesWithLyrics = lines.filter(
      (line) => line.trim().length > 0
    ).length;

    if (
      lengthOfLinesWithLyrics === Object.keys(lineTimestamps).length &&
      lineTimestamps[
        Object.keys(lineTimestamps)[Object.keys(lineTimestamps).length - 1]
      ]?.end
    ) {
      setFinishedSyncing(true);
    } else {
      setFinishedSyncing(false);
    }

    // Add event listener when component mounts
    window.addEventListener("keydown", onSpacePress);

    // Cleanup event listener when component unmounts
    return () => {
      window.removeEventListener("keydown", onSpacePress);
    };
  }, [
    currentLine,
    lines,
    currentTimestamp,
    lineTimestamps,
    finishedSyncing,
    isPlaying,
  ]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      // if backspace or delete key is pressed
      if (event.code === "Backspace" || event.code === "Delete") {
        if (textFiledIsActive || editingLine) {
          return;
        }
        removeLineTimestamp(currentLine);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [removeLineTimestamp, currentLine, textFiledIsActive]);

  return (
    <>
      <Typography className={classes.commandsTitle}>
        Sync Lyrics with Audio
      </Typography>
      <SyncFileInstructions />
      <Box
        sx={{
          width: "100%",
          mt: 2,
          padding: "16px 5px",
          border: "1px solid #ccc",
          borderRadius: "10px",
          height: "calc(100vh - 420px)",
        }}
      >
        <Box
          sx={{
            height: "100%",
            overflowY: "scroll",
            mt: "5px",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {lines.map((line, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                borderRadius: "5px",
                // width: '100%'
                width: "max-content",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                {/* Start timestamp */}
                {lineTimestamps[index]?.start !== null &&
                lineTimestamps[index]?.start !== undefined ? (
                  <Timestamp
                    timestamp={lineTimestamps[index]?.start}
                    index={index}
                    lineTimestamps={lineTimestamps}
                    type="start"
                    setLineTimestamps={setLineTimestamps}
                    setTextFiledIsActive={setTextFiledIsActive}
                  />
                ) : (
                  ""
                )}

                {/* Show End timestamp if it exists, and Delete icon for the last item */}
                {lineTimestamps[index]?.end !== null &&
                lineTimestamps[index]?.end !== undefined ? (
                  <Timestamp
                    timestamp={lineTimestamps[index]?.end}
                    index={index}
                    lineTimestamps={lineTimestamps}
                    type="end"
                    setLineTimestamps={setLineTimestamps}
                    setTextFiledIsActive={setTextFiledIsActive}
                  />
                ) : null}
                {/* Show Delete icon only for the last item in timestamps */}
                {index ===
                parseInt(
                  Object.keys(lineTimestamps)[
                    Object.keys(lineTimestamps).length - 1
                  ]
                ) && (
                  <IconButton
                    color="error"
                    onClick={() => removeLineTimestamp(index)}
                    size="small"
                    sx={{ ml: 1, display: "inline-flex", alignSelf: "center" }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              {editingLine === index ? (
                <TextField
                  value={lines[index]}
                  onChange={(e) => handleEditLine(index, e.target.value)}
                  onBlur={() => setEditingLine(null)}
                  variant="outlined"
                  size="small"
                  autoFocus
                  sx={{
                    textAlign: "center",
                    fontSize: "18px",
                    width: `${lines[index].length + 1}ch`, // Set width based on content length
                    maxWidth: "90%",
                    borderRadius: "5px",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#aaa",
                        borderWidth: "1.5px",
                        borderRadius: "5px",
                      },
                      "&:hover fieldset": {
                        borderColor: "#666",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#333",
                      },
                    },
                  }}
                />
              ) : (
                <Typography
                  variant="body1"
                  sx={{
                    color: index === currentLine ? "black" : "gray",
                    textAlign: "center",
                    fontSize: "18px",
                    cursor: "pointer",
                  }}
                  className={`line-${index}`}
                  onClick={(e) => {
                    // If line has a start timestamp, play from there
                    if (lineTimestamps[index]?.start !== null && 
                        lineTimestamps[index]?.start !== undefined) {
                      e.preventDefault();
                      const startTime = lineTimestamps[index].start;
                      updateAudioTime(startTime);
                      setCurrentLine(index);
                      // Play audio if not already playing
                      if (audioRef.current && !isPlaying) {
                        if (playAudio) {
                          playAudio();
                        } else if (audioRef.current) {
                          setIsPlaying(true);
                          audioRef.current.play();
                        }
                      }
                    } else {
                      // If no start timestamp, allow editing
                      setEditingLine(index);
                    }
                  }}
                  onDoubleClick={() => {
                    // Double click always allows editing
                    setEditingLine(index);
                  }}
                >
                  {line}
                </Typography>
              )}

              {/* Line text */}
              {/* <Typography
                                variant="body1"
                                sx={{
                                    color: index === currentLine ? 'black' : 'gray',
                                    textAlign: 'center',
                                    fontSize: '18px'
                                }}
                                className={`line-${index}`}
                            >
                                {line}
                            </Typography> */}
            </Box>
          ))}
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: { xs: 1, sm: 2 },
          flexWrap: "wrap",
          mt: 2,
          width: "100%",
        }}
      >
        <Button
          variant="contained"
          onClick={syncNextLine}
          disabled={finishedSyncing}
          startIcon={<SkipNextIcon />}
          sx={{
            backgroundColor: "#142C5C",
            color: "#fff",
            borderRadius: "6px",
            textTransform: "none",
            fontWeight: 600,
            paddingX: { xs: 2, sm: 3 },
            paddingY: { xs: 1, sm: 1.25 },
            fontSize: { xs: "13px", sm: "14px" },
            minHeight: "44px",
            "&:hover": { backgroundColor: "#0f2247" },
            "&:disabled": {
              backgroundColor: "#142C5C !important",
              color: "white !important",
              opacity: 0.5,
            },
          }}
        >
          Sync Next Line
        </Button>
        <Button
          variant="outlined"
          onClick={removeLastTimestamp}
          disabled={Object.keys(lineTimestamps).length === 0}
          startIcon={<UndoIcon />}
          sx={{
            borderColor: "#142C5C",
            color: "#142C5C",
            borderRadius: "6px",
            textTransform: "none",
            fontWeight: 600,
            paddingX: { xs: 2, sm: 3 },
            paddingY: { xs: 1, sm: 1.25 },
            fontSize: { xs: "13px", sm: "14px" },
            minHeight: "44px",
            "&:hover": {
              borderColor: "#0f2247",
              backgroundColor: "rgba(20, 44, 92, 0.06)",
            },
          }}
        >
          Remove Last
        </Button>
      </Box>
    </>
  );
};

export default SyncLyricsUI;
